import { useState, useCallback } from "react";
import {
  useNodesState,
  useEdgesState,
  addEdge,
  applyNodeChanges,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
} from "reactflow";
import type {
  WorkflowData,
  WorkflowEdge,
  NodeData,
} from "../types/workflow.types";
import type { WorkflowCategory } from "../utils/enums/index";
import {
  sanitizeWorkflowForBackend,
  hydrateWorkflowFromBackend,
} from "../utils/workflowSanitizer";

export const useWorkflow = (
  workflowType?: WorkflowCategory,
  nodeConfigs?: any,
  nodeDynamicDataMap?: any
) => {
  const [nodes, setNodes] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([]);
  const [workflowName, setWorkflowName] = useState("Untitled Workflow");
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => {
        const updated = applyNodeChanges(changes, nds);
        setSelectedNodes(updated.filter((n) => n.selected));
        return updated;
      });
    },
    [setNodes]
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
      setSelectedEdgeId(null);
    },
    [setEdges]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: "custom",
            animated: true,
            style: { stroke: "#404040", strokeWidth: 2 },
            data: { onDelete: deleteEdge },
          },
          eds
        )
      );
    },
    [setEdges, deleteEdge]
  );

  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
  }, []);

  const addNode = useCallback(
    (nodeData: NodeData, position: { x: number; y: number }) => {
      const newNode: Node<NodeData> = {
        id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: nodeData.type,
        position,
        data: {
          ...nodeData,
          config: nodeData.config || {},
          fields: nodeData.fields || [],
          contextFields: nodeData.contextFields || [],
          dynamicData: nodeData.dynamicData || {},
        },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes]
  );

  const updateNodeData = useCallback(
    (nodeId: string, newConfig: Record<string, any>) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, config: newConfig } }
            : node
        )
      );
    },
    [setNodes]
  );

  const deleteNode = useCallback(
    (nodeId?: string) => {
      const idsToDelete = nodeId ? [nodeId] : selectedNodes.map((n) => n.id);
      if (!idsToDelete.length) return;
      setNodes((nds) => nds.filter((node) => !idsToDelete.includes(node.id)));
      setEdges((eds) =>
        eds.filter(
          (edge) =>
            !idsToDelete.includes(edge.source) &&
            !idsToDelete.includes(edge.target)
        )
      );
      setSelectedNodes([]);
      if (nodeId && selectedNodeId === nodeId) setSelectedNodeId(null);
    },
    [setNodes, setEdges, selectedNodeId, selectedNodes]
  );

  const clearWorkflow = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setSelectedNodes([]);
    setWorkflowName("Untitled Workflow");
    setWorkflowId(null);
  }, [setNodes, setEdges]);

  const exportWorkflow = useCallback((): WorkflowData => {
    const workflow: WorkflowData = {
      name: workflowName,
      nodes,
      edges,
      version: 1,
    };
    if (workflowType) workflow.workflow_type = workflowType;
    if (workflowId && typeof workflowId === "number") workflow.id = workflowId;
    return workflow;
  }, [nodes, edges, workflowName, workflowId, workflowType]);

  const exportWorkflowForBackend = useCallback(() => {
    return sanitizeWorkflowForBackend(exportWorkflow());
  }, [exportWorkflow]);

  const importWorkflow = useCallback(
    (workflow: WorkflowData, shouldHydrate = false) => {
      if (!workflow) {
        // console.warn("importWorkflow: No workflow provided, skipping import");
        return;
      }

      let workflowToImport = workflow;
      if (shouldHydrate && nodeConfigs) {
        workflowToImport = hydrateWorkflowFromBackend(
          workflow,
          nodeConfigs,
          nodeDynamicDataMap || {}
        );
      }

      setWorkflowName(workflowToImport.name || "Untitled Workflow");
      setWorkflowId(workflowToImport.id || null);

      if (workflowToImport.nodes) {
        setNodes(workflowToImport.nodes);
      }

      if (workflowToImport.edges) {
        // Enrich edges with onDelete callback and other properties
        const enrichedEdges = workflowToImport.edges.map((edge) => ({
          ...edge,
          type: edge.type || "custom",
          animated: edge.animated !== false,
          style: edge.style || { stroke: "#404040", strokeWidth: 2 },
          data: {
            ...edge.data,
            onDelete: deleteEdge,
          },
        }));
        setEdges(enrichedEdges);
      }

      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setSelectedNodes([]);
    },
    [setNodes, setEdges, nodeConfigs, nodeDynamicDataMap, deleteEdge]
  );

  const getSelectedNode = useCallback(() => {
    return selectedNodeId
      ? nodes.find((node) => node.id === selectedNodeId) || null
      : null;
  }, [nodes, selectedNodeId]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onEdgeClick,
    addNode,
    updateNodeData,
    deleteNode,
    deleteEdge,
    clearWorkflow,
    exportWorkflow,
    exportWorkflowForBackend,
    importWorkflow,
    selectedNodeId,
    setSelectedNodeId,
    selectedEdgeId,
    setSelectedEdgeId,
    getSelectedNode,
    workflowName,
    setWorkflowName,
    workflowId,
    setWorkflowId,
    selectedNodes,
    setSelectedNodes,
  };
};
